/**
 * tick.ts — the orchestrator. Runs every system once per fixed tick in
 * dependency order. This is the single function fed to the GameLoop (and thus
 * shared by live play and offline catch-up).
 */
import type { GameState } from '../core/GameState';
import type { TickContext } from '../core/GameLoop';
import { pruneModifiers } from './modifiers';
import { computeDerived } from './derived';
import { tickMining } from './mining';
import { tickSmelting } from './production';
import { tickResearch } from './research';
import { tickMarket, tickAutosell } from './economy';
import { tickEvents } from './events';
import { tickQuests } from './quests';
import { checkAchievements } from './achievements';
import { tickNuggets } from './nuggets';
import { tickChallenges } from './challenges';
import { tickAutomation } from './automation';

export function tickGame(state: GameState, dtMs: number, ctx: TickContext): void {
  const bus = ctx.bus;

  pruneModifiers(state);

  const d = computeDerived(state);
  tickMining(state, dtMs, d);
  tickSmelting(state, dtMs, d);
  tickResearch(state, dtMs, d);
  tickAutosell(state, d);

  tickMarket(state, dtMs);
  tickEvents(state, bus);
  tickQuests(state, bus);
  checkAchievements(state, bus);
  // Appended LAST so its rng draws sit at a stable position in the stream
  // (preserves determinism for existing systems).
  tickNuggets(state, bus);
  tickChallenges(state, bus);
  // Automation is RNG-free and off by default → no determinism impact.
  tickAutomation(state, ctx);
}
